import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";

/**
 * OverrideReasonModal — fleet-wide. Any time a user tries to proceed past a
 * guardrail (pricing outside market range, Red-flagged partner going live,
 * etc.) this modal collects a required 10-500 char reason BEFORE the action
 * is submitted.
 *
 * Props:
 *   open:        boolean
 *   title:       string
 *   subtitle:    string (what the user is overriding)
 *   aiRationale: optional string shown ABOVE the input so the user sees the
 *                system's reasoning before typing theirs
 *   placeholder: input placeholder
 *   confirmLabel: button copy
 *   onCancel():  dismiss
 *   onConfirm(reason): called with the trimmed reason — parent handles the API call
 *   busy: boolean — disables the confirm button while parent is submitting
 *
 * Component is theme-agnostic (dark overlay + light card) so it works on
 * both the public cream pages and the partner-portal obsidian theme.
 */
const MIN_LEN = 10;
const MAX_LEN = 500;

export default function OverrideReasonModal({
    open,
    title = "Please confirm with a brief reason",
    subtitle,
    aiRationale,
    placeholder = "Share your thinking in 1-3 sentences…",
    confirmLabel = "Confirm & Proceed",
    cancelLabel = "Cancel",
    onCancel,
    onConfirm,
    busy = false,
    tone = "amber", // amber | rose — colors the AI rationale callout
    testId = "override-reason-modal",
}) {
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (open) setReason("");
    }, [open]);

    if (!open) return null;

    const trimmed = reason.trim();
    const tooShort = trimmed.length < MIN_LEN;
    const tooLong = trimmed.length > MAX_LEN;
    const disabled = busy || tooShort || tooLong;

    const toneBg =
        tone === "rose"
            ? "bg-rose-50 border-rose-300 text-rose-900"
            : "bg-amber-50 border-amber-300 text-amber-900";

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            data-testid={testId}
        >
            <div className="bg-white max-w-md w-full rounded-sm shadow-2xl overflow-hidden">
                <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
                    <div>
                        <div className="inline-flex items-center gap-2 text-amber-600 text-[0.6rem] tracking-[0.3em] uppercase font-semibold">
                            <AlertTriangle size={12} /> Guardrail
                        </div>
                        <h3 className="font-serif text-xl text-forest-deep mt-2 leading-tight">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-xs text-forest-deep/70 mt-1.5 leading-relaxed">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="shrink-0 text-forest-deep/40 hover:text-forest-deep"
                        aria-label="Close"
                        data-testid={`${testId}-close`}
                    >
                        <X size={16} />
                    </button>
                </header>

                {aiRationale && (
                    <div
                        className={`mx-6 mb-4 p-3 border rounded-sm text-xs leading-relaxed ${toneBg}`}
                        data-testid={`${testId}-ai-rationale`}
                    >
                        <p className="font-semibold text-[0.6rem] tracking-[0.25em] uppercase mb-1.5">
                            What the system noticed
                        </p>
                        {aiRationale}
                    </div>
                )}

                <div className="px-6 pb-4">
                    <label className="block text-[0.6rem] tracking-[0.25em] uppercase text-forest-deep/70 mb-1.5">
                        Your reason <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={placeholder}
                        maxLength={MAX_LEN + 50}
                        data-testid={`${testId}-input`}
                        className="w-full px-3 py-2 border border-forest-deep/20 rounded-sm text-sm text-forest-deep focus:outline-none focus:border-gold"
                    />
                    <div className="flex items-center justify-between mt-1 text-[0.6rem] text-forest-deep/50">
                        <span>
                            Min {MIN_LEN} chars · 1-3 sentences recommended
                        </span>
                        <span
                            className={
                                tooLong ? "text-rose-500 font-semibold" : ""
                            }
                        >
                            {trimmed.length} / {MAX_LEN}
                        </span>
                    </div>
                </div>

                <footer className="px-6 py-4 bg-forest-deep/5 flex justify-end gap-2 border-t border-forest-deep/10">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        data-testid={`${testId}-cancel`}
                        className="btn-pill btn-ghost text-xs"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(trimmed)}
                        disabled={disabled}
                        data-testid={`${testId}-confirm`}
                        className="btn-pill btn-gold text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            confirmLabel
                        )}
                    </button>
                </footer>
            </div>
        </div>
    );
}
